/* Optional enhancement. Every entry, source and reading path works without JS. */
(() => {
  'use strict';
  const normalise = text => text.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/ł/g, 'l').replace(/ß/g, 'ss').replace(/\s+/g, ' ').trim();
  for (const link of document.querySelectorAll('.atlas-language-nav a')) {
    link.addEventListener('click', () => {
      const destination = new URL(link.href);
      const current = new URL(location.href);
      for (const key of ['family', 'scope']) {
        if (current.searchParams.has(key)) destination.searchParams.set(key, current.searchParams.get(key));
      }
      if (link.lang === document.documentElement.lang && current.searchParams.has('q')) destination.searchParams.set('q', current.searchParams.get('q'));
      destination.hash = current.hash;
      link.href = destination.href;
    });
  }
  for (const root of document.querySelectorAll('[data-atlas]')) {
    if (root.dataset.atlasReady) continue;
    root.dataset.atlasReady = 'true';
    const progress = root.querySelector('[data-reader-progress]');
    if (progress) {
      const number = new Intl.NumberFormat(root.dataset.locale || document.documentElement.lang || 'en', {maximumFractionDigits: 0});
      const value = progress.querySelector('[data-progress-value]');
      let pending = false, previous = -1;
      const paintProgress = () => {
        pending = false;
        const page = document.scrollingElement || document.documentElement;
        const distance = Math.max(0, page.scrollHeight - page.clientHeight);
        const percent = distance ? Math.min(100, Math.max(0, Math.round(page.scrollTop / distance * 100))) : 100;
        if (percent === previous) return;
        previous = percent;
        progress.style.setProperty('--atlas-progress', percent / 100);
        value.textContent = `${number.format(percent)}%`;
        progress.setAttribute('aria-label', progress.dataset.progressLabel.replace('{percent}', number.format(percent)));
      };
      const queueProgress = () => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(paintProgress);
      };
      progress.hidden = false;
      paintProgress();
      window.addEventListener('scroll', queueProgress, {passive: true});
      window.addEventListener('resize', queueProgress, {passive: true});
      if ('ResizeObserver' in window) new ResizeObserver(queueProgress).observe(root);
      progress.addEventListener('click', () => {
        const url = new URL(location.href);
        url.hash = '';
        history.replaceState(null, '', url);
        const heading = root.querySelector('h1');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({preventScroll: true});
        }
        window.scrollTo({top: 0, left: 0, behavior: 'instant'});
        queueProgress();
      });
    }
    const share = root.querySelector('[data-reader-share]');
    if (share) {
      const button = share.querySelector('[data-share-story]');
      const panel = share.querySelector('[data-share-panel]');
      const status = share.querySelector('[data-share-status]');
      const address = share.querySelector('[data-share-address]');
      const localPreview = location.protocol === 'file:' || /^(localhost|127\.\d+\.\d+\.\d+|\[::1\])$/.test(location.hostname);
      const enhancedSharing = share.dataset.sharingV9 === 'true';
      const validPublicURL = value => {
        try {
          const url = new URL(value);
          return url.protocol === 'https:' && !url.username && !url.password &&
            !/^(localhost|127\.\d+\.\d+\.\d+|\[::1\])$/.test(url.hostname) &&
            !url.hostname.endsWith('.local') && !url.hostname.endsWith('.localhost');
        } catch { return false; }
      };
      const publicReady = !localPreview && (!enhancedSharing || (
        share.dataset.publicShareState === 'verified' && validPublicURL(share.dataset.publicShareUrl) &&
        validPublicURL(share.dataset.publicShareImage)));
      const systemShare = !enhancedSharing && publicReady && typeof navigator.share === 'function';
      const title = share.dataset.shareTitle || root.querySelector('h1')?.textContent.trim() || document.title;
      const shareURL = () => {
        const url = new URL(publicReady && enhancedSharing ? share.dataset.publicShareUrl : location.href);
        // Keep the selected language and useful atlas filters, omit theme-review
        // and advertising parameters from a reader's shared link.
        for (const key of [...url.searchParams.keys()]) {
          if (!['q', 'family', 'scope'].includes(key)) url.searchParams.delete(key);
        }
        return url.href;
      };
      const showAddress = () => {
        panel.hidden = false;
        address.hidden = false;
        const input = address.querySelector('input');
        input.value = shareURL();
        input.focus({preventScroll: true});
        input.select();
        if (!systemShare) button.setAttribute('aria-expanded', 'true');
      };
      share.hidden = false;
      if (!systemShare) button.setAttribute('aria-expanded', 'false');
      share.querySelector('[data-share-preview]').hidden = enhancedSharing || !localPreview;
      const localNotice = share.querySelector('[data-share-local-notice]');
      if (localNotice) localNotice.hidden = !localPreview;
      const pendingNotice = share.querySelector('[data-share-pending-notice]');
      if (pendingNotice) pendingNotice.hidden = localPreview || publicReady;
      for (const channel of share.querySelectorAll('[data-share-channel]')) {
        channel.hidden = !enhancedSharing && localPreview;
        channel.disabled = !publicReady;
        channel.addEventListener('click', () => {
          if (!publicReady) return;
          const url = shareURL();
          const destinations = {
            whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' — ' + url)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
            pinterest: `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(share.dataset.publicShareImage || '')}&description=${encodeURIComponent(title + ' — ' + (share.dataset.shareDescription || ''))}`,
          };
          const destination = destinations[channel.dataset.shareChannel];
          if (!destination) return;
          window.open(destination, '_blank', 'noopener,noreferrer');
        });
      }
      const device = share.querySelector('[data-share-device]');
      if (device) {
        device.hidden = !publicReady || typeof navigator.share !== 'function';
        device.addEventListener('click', async () => {
          if (!publicReady || typeof navigator.share !== 'function') return;
          try { await navigator.share({title, text: share.dataset.shareDescription || '', url: shareURL()}); }
          catch (error) { if (error.name !== 'AbortError') status.textContent = share.dataset.shareError; }
        });
      }
      for (const copy of share.querySelectorAll('[data-copy-link]')) copy.addEventListener('click', async () => {
        status.textContent = '';
        try {
          await navigator.clipboard.writeText(shareURL());
          status.textContent = share.dataset.copied + (localPreview ? '. ' + share.dataset.previewNotice : '');
        } catch {
          status.textContent = share.dataset.copyFailed;
          showAddress();
        }
      });
      button.addEventListener('click', async () => {
        status.textContent = '';
        if (systemShare) {
          try { await navigator.share({title, url: shareURL()}); }
          catch (error) {
            if (error.name === 'AbortError') return;
            status.textContent = share.dataset.shareError;
            panel.hidden = false;
          }
          return;
        }
        panel.hidden = !panel.hidden;
        button.setAttribute('aria-expanded', String(!panel.hidden));
        if (!enhancedSharing && localPreview && !panel.hidden) showAddress();
      });
      share.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || panel.hidden) return;
        panel.hidden = true;
        if (!systemShare) button.setAttribute('aria-expanded', 'false');
        button.focus();
      });
    }
    const form = root.querySelector('[data-filters]');
    if (form) {
      const cards = [...root.querySelectorAll('[data-entry]')];
      const groups = [...root.querySelectorAll('[data-family-group]')];
      const q = form.elements.namedItem('q');
      const family = form.elements.namedItem('family');
      const scope = form.elements.namedItem('scope');
      const results = root.querySelector('[data-results]');
      const empty = root.querySelector('[data-empty]');
      const familyChoices = [...form.querySelectorAll('[data-family-choice]')];
      const scopeChoices = [...form.querySelectorAll('[data-scope-choice]')];
      const familySummary = form.querySelector('[data-family-summary]');
      const familyPanel = form.querySelector('.atlas-family-details');
      const surprise = root.querySelector('[data-surprise]');
      function update(writeURL = true) {
        const words = normalise(q.value).split(' ').filter(Boolean);
        let count = 0;
        for (const card of cards) {
          const match = words.every(word => card.dataset.search.includes(word)) &&
            (family.value === 'all' || card.dataset.family === family.value) &&
            (scope.value === 'all' || card.dataset.scope === scope.value);
          card.hidden = !match;
          if (match) count++;
        }
        for (const group of groups) group.hidden = ![...group.querySelectorAll('[data-entry]')].some(card => !card.hidden);
        const category = new Intl.PluralRules(root.dataset.locale || document.documentElement.lang || 'en').select(count);
        const template = root.dataset[`result${category[0].toUpperCase()}${category.slice(1)}`] || root.dataset.resultOther;
        results.textContent = template ? template.replace('{count}', count) : `${count} ${count === 1 ? 'ingredient' : 'ingredients'} to explore`;
        empty.hidden = count !== 0;
        for (const button of familyChoices) button.setAttribute('aria-pressed', String(button.dataset.familyChoice === family.value));
        const chosenFamily = familyChoices.find(button => button.dataset.familyChoice === family.value);
        if (familySummary && chosenFamily) familySummary.textContent = chosenFamily.querySelector('span').textContent;
        for (const button of scopeChoices) button.setAttribute('aria-pressed', String(button.dataset.scopeChoice === scope.value));
        if (surprise) {
          surprise.hidden = count === 0;
          const first = cards.find(card => !card.hidden);
          if (first) surprise.href = first.querySelector('a').href;
        }
        for (const a of root.querySelectorAll('.atlas-family-links a')) {
          const target = root.querySelector(a.getAttribute('href'));
          a.hidden = !target || target.hidden;
        }
        if (writeURL) {
          const url = new URL(location.href);
          for (const [key, value] of [['q', q.value.trim()], ['family', family.value], ['scope', scope.value]]) {
            if (value && value !== 'all') url.searchParams.set(key, value); else url.searchParams.delete(key);
          }
          history.replaceState(null, '', url);
        }
      }
      function fromURL() {
        const params = new URL(location.href).searchParams;
        q.value = params.get('q') || '';
        for (const control of [family, scope]) {
          const value = params.get(control.name);
          const choices = control.name === 'family' ? familyChoices.map(button => button.dataset.familyChoice) : scopeChoices.map(button => button.dataset.scopeChoice);
          control.value = choices.includes(value) ? value : 'all';
        }
        update(false);
      }
      form.hidden = false;
      const noJsFamilies = root.querySelector('[data-nojs-families]');
      if (noJsFamilies) noJsFamilies.hidden = true;
      if (familyPanel) {
        const compactFamilies = matchMedia('(max-width:768px)');
        const setPanel = () => { familyPanel.open = !compactFamilies.matches; };
        setPanel();
        compactFamilies.addEventListener('change', setPanel);
      }
      for (const button of familyChoices) button.addEventListener('click', () => { family.value = button.dataset.familyChoice; update(); });
      for (const button of scopeChoices) button.addEventListener('click', () => { scope.value = button.dataset.scopeChoice; update(); });
      if (surprise) surprise.addEventListener('click', () => {
        const available = cards.filter(card => !card.hidden);
        if (!available.length) return;
        const alternatives = available.filter(card => card.querySelector('a').href !== surprise.href);
        const pool = alternatives.length ? alternatives : available;
        surprise.href = pool[Math.floor(Math.random() * pool.length)].querySelector('a').href;
      });
      form.addEventListener('submit', event => { event.preventDefault(); update(); });
      form.addEventListener('input', () => update());
      form.addEventListener('change', () => update());
      form.addEventListener('reset', () => {
        setTimeout(() => {
          q.value = '';
          family.value = 'all';
          scope.value = 'all';
          update();
          q.focus({preventScroll: true});
        }, 0);
      });
      window.addEventListener('popstate', fromURL);
      fromURL();
    }
    const contents = root.querySelector('.atlas-contents');
    if (contents) {
      const mobile = matchMedia('(max-width:768px)');
      const setContents = () => { contents.open = !mobile.matches; };
      setContents();
      mobile.addEventListener('change', setContents);
      const links = [...contents.querySelectorAll('a')];
      for (const link of links) link.addEventListener('click', event => {
        const target = root.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        if (mobile.matches) contents.open = false;
        target.setAttribute('tabindex', '-1');
        target.focus({preventScroll: true});
        target.scrollIntoView({block: 'start'});
        history.replaceState(null, '', link.getAttribute('href'));
      });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(items => {
          const visible = items.filter(item => item.isIntersecting).sort((a,b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
          if (!visible) return;
          for (const a of links) {
            if (a.hash === '#' + visible.target.id) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
          }
        }, {rootMargin: '-15% 0px -65% 0px'});
        for (const link of links) {
          const target = root.querySelector(link.hash);
          if (target) observer.observe(target);
        }
      }
    }
    // Match the actual owning theme's sticky header without altering its styles.
    const measureHeader = () => {
      let bottom = parseFloat(getComputedStyle(document.body).getPropertyValue('--height-header')) || 0;
      for (const header of document.querySelectorAll('header,sticky-header,.header-sticky,.header-middle')) {
        if (root.contains(header)) continue;
        const style = getComputedStyle(header), box = header.getBoundingClientRect();
        if (['fixed','sticky'].includes(style.position) && box.top <= 2 && box.bottom > 0) bottom = Math.max(bottom, box.bottom);
      }
      root.style.setProperty('--atlas-anchor-offset', `${Math.ceil(bottom + 24)}px`);
    };
    measureHeader();
    window.addEventListener('resize', measureHeader, {passive: true});
    window.addEventListener('scroll', measureHeader, {passive: true});
  }
})();
